import { useId, useMemo, useRef, useState, type ReactNode } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { Pencil } from 'lucide-react'
import { standardSchemaResolver } from '@hookform/resolvers/standard-schema'
import { MetricCombobox } from '@/components/custom/metrics/metric-combobox'
import {
  DirectionControl,
  GoalTypePicker,
} from '@/components/custom/ogsm/objective-choice-controls'
import { ScorecardDatePicker } from '@/components/custom/scorecard/scorecard-date-picker'
import { ScorecardTargetValueInput } from '@/components/custom/scorecard/scorecard-target-value-input'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  FormBody,
  FormFooter,
  OgsmDialogContent,
  OgsmDialogHeader,
} from '@/components/custom/ogsm/ogsm-dialog-shell'
import { Dialog } from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import { Textarea } from '@/components/ui/textarea'
import { Typography } from '@/components/ui/typography'
import { ogsmErrorMessage } from '@/features/ogsm/errors'
import {
  TEXT_GOAL_MAX,
  defaultEditObjectiveValues,
  defaultObjectiveFormValues,
  editObjectiveSchema,
  editsMetric,
  GOAL_TYPES,
  goalTypeOf,
  goalTypesFor,
  isNumericTarget,
  metricUnitLabel,
  objectiveFormSchemaFor,
  objectivePreview,
  toCreateNodeInput,
  toUpdateObjectiveInput,
  type EditObjectiveValues,
  type GoalType,
  type ObjectiveDialogTarget,
  type ObjectiveFormValues,
} from '@/features/ogsm/new-objective'
import { OGSM_COPY } from '@/features/ogsm/ogsm-labels'
import {
  formatTarget,
  toScorecardDataType,
} from '@/features/ogsm/target-format'
import { useOgsmApi } from '@/hooks/use-ogsm'
import {
  useCreateOgsmNodeMutation,
  useUpdateOgsmNodeMutation,
} from '@/hooks/use-ogsm-data'
import { useScorecardMetricOptions } from '@/hooks/use-scorecard'
import type { ScorecardMetricOptionsResponseDto } from '@/services/scorecard.service'
import type { OgsmBoardNode, OgsmSession } from '@/types/api'

export interface NewObjectiveDialogProps {
  companyId: string
  session: OgsmSession
  /** null while closed. */
  target: ObjectiveDialogTarget | null
  onClose: () => void
  /** Edit only: offers "Revise goal/measure" as a button, opened once this dialog has closed. */
  onRevise?: (node: OgsmBoardNode) => void
}

const VERB_LIST_ID = 'ogsm-objective-verbs'

function useMetricOptions(
  companyId: string,
  enabled: boolean,
  includeMetricLabelId?: string
) {
  const metrics = useScorecardMetricOptions(
    companyId,
    'MONTHLY',
    enabled,
    includeMetricLabelId
  )
  const metricOptions = metrics.data?.options
  const options = useMemo(
    () =>
      (metricOptions ?? []).map((option) => ({
        id: option.metricLabelId,
        label: option.labeledAs,
        labeledAs: option.labeledAs,
        sectionName: option.globalSectionName ?? 'Metrics',
        category: option.metricType?.category,
        description:
          option.metricType?.name ?? option.globalSectionName ?? undefined,
        sortOrder: option.sortOrder ?? null,
      })),
    [metricOptions]
  )
  return { metricOptions, options, isLoading: metrics.isLoading }
}

function findMetric(
  metricOptions: ScorecardMetricOptionsResponseDto['options'] | undefined,
  metricLabelId: string | null
) {
  return metricOptions?.find((option) => option.metricLabelId === metricLabelId)
}

function Required() {
  return <span className="ml-1 text-destructive">*</span>
}

function NoteBox({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-r-md border-l-2 border-primary/45 bg-primary/5 px-2.5 py-2">
      <Typography variant="body-sm" className="text-primary/60">
        {children}
      </Typography>
    </div>
  )
}

/** Edit cannot change the type: the PATCH omits it. */
function LockedGoalType({ type }: { type: GoalType }) {
  return (
    <div className="flex flex-col gap-3">
      <Label>{OGSM_COPY.goalType}</Label>
      <GoalTypePicker
        types={GOAL_TYPES}
        value={type}
        disabled
        description={OGSM_COPY.goalTypeLocked}
      />
    </div>
  )
}

function FormError({ error }: { error: unknown }) {
  return (
    <Typography variant="body-sm" role="alert" className="text-destructive">
      {ogsmErrorMessage(error)}
    </Typography>
  )
}

function CreateObjectiveForm({
  companyId,
  session,
  parent,
  onDone,
}: {
  companyId: string
  session: OgsmSession
  /** Set for a child: the new node is created under it. */
  parent?: OgsmBoardNode
  onDone: () => void
}) {
  const mode = parent ? 'child' : 'root'
  const submitLabel =
    mode === 'child' ? OGSM_COPY.createStrategy : OGSM_COPY.createObjective
  const statementLabel =
    mode === 'child' ? OGSM_COPY.strategyText : OGSM_COPY.objectiveText
  const api = useOgsmApi(companyId)
  const { metricLinking } = api
  const create = useCreateOgsmNodeMutation(api, session.id)
  const descriptorId = useId()
  const form = useForm<ObjectiveFormValues>({
    resolver: standardSchemaResolver(objectiveFormSchemaFor(mode)),
    defaultValues: defaultObjectiveFormValues(
      session.periodEnd,
      mode,
      metricLinking
    ),
  })
  const [
    goalType,
    direction,
    metricLabelId,
    title,
    verb,
    descriptor,
    targetText,
  ] = useWatch({
    control: form.control,
    name: [
      'goalType',
      'direction',
      'metricLabelId',
      'title',
      'verb',
      'descriptor',
      'targetText',
    ],
  })
  const isMetric = goalType === 'METRIC'
  const isStatement = goalType === 'DATE' || goalType === 'TEXT'
  const metrics = useMetricOptions(companyId, isMetric && metricLinking)
  const metric = findMetric(metrics.metricOptions, metricLabelId)
  const preview = objectivePreview(
    {
      ...form.getValues(),
      goalType,
      direction,
      metricLabelId,
      title,
      verb,
      descriptor,
    },
    metric?.labeledAs
  )

  const directionField = (
    <FormField
      control={form.control}
      name="direction"
      render={({ field }) => (
        <FormItem>
          <FormLabel>
            {OGSM_COPY.direction}
            <Required />
          </FormLabel>
          <DirectionControl value={field.value} onChange={field.onChange} />
        </FormItem>
      )}
    />
  )
  const goalValueField = (
    <FormField
      key="value"
      control={form.control}
      name="targetValue"
      render={({ field }) => (
        <FormItem className="min-w-0">
          <FormLabel>
            {OGSM_COPY.goalValue}
            <Required />
          </FormLabel>
          <FormControl>
            <ScorecardTargetValueInput
              ref={field.ref}
              name={field.name}
              value={field.value ?? Number.NaN}
              onChange={(value) =>
                field.onChange(Number.isFinite(value) ? value : null)
              }
              onBlur={field.onBlur}
              dataType={
                goalType === 'METRIC'
                  ? metric?.dataType
                  : toScorecardDataType(goalType)
              }
              affix="addon"
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  )
  const dateField = (
    <FormField
      key="date"
      control={form.control}
      name="targetDate"
      render={({ field }) => (
        <FormItem className="min-w-0">
          <FormLabel>
            {goalType === 'DATE' ? OGSM_COPY.achievedBy : OGSM_COPY.targetDate}
            <Required />
          </FormLabel>
          <FormControl>
            <ScorecardDatePicker
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  )

  return (
    <Form {...form}>
      <form
        className="flex min-h-0 flex-1 flex-col"
        onSubmit={form.handleSubmit((values) =>
          create.mutate(
            toCreateNodeInput(values, {
              parentId: parent?.id,
              metricName: metric?.labeledAs ?? '',
            }),
            { onSuccess: onDone }
          )
        )}
      >
        <FormBody>
          <FormField
            control={form.control}
            name="goalType"
            render={({ field }) => (
              <FormItem className="gap-3">
                <FormLabel>{OGSM_COPY.goalType}</FormLabel>
                <GoalTypePicker
                  types={goalTypesFor(mode, metricLinking)}
                  value={field.value}
                  description={OGSM_COPY.goalTypeDescriptions[field.value]}
                  onChange={(next) => {
                    form.setValue('goalType', next)
                    form.clearErrors()
                  }}
                />
              </FormItem>
            )}
          />

          <Separator className="bg-primary/10" />

          {/* Keyed so each goal type mounts its own fields instead of React reusing them. */}
          {isStatement ? (
            <div key="statement" className="flex flex-col gap-4">
              <div className="flex flex-col gap-2">
                <Label
                  htmlFor={descriptorId}
                  className={
                    form.formState.errors.descriptor
                      ? 'text-destructive'
                      : undefined
                  }
                >
                  {statementLabel}
                  <Required />
                </Label>
                <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
                  <FormField
                    control={form.control}
                    name="verb"
                    render={({ field }) => (
                      <FormItem className="shrink-0 sm:w-38">
                        <FormControl>
                          <Input
                            {...field}
                            aria-label={OGSM_COPY.verb}
                            placeholder={OGSM_COPY.verbPlaceholder}
                            list={VERB_LIST_ID}
                            autoComplete="off"
                          />
                        </FormControl>
                        <datalist id={VERB_LIST_ID}>
                          {OGSM_COPY.verbSuggestions.map((suggestion) => (
                            <option key={suggestion} value={suggestion} />
                          ))}
                        </datalist>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="descriptor"
                    render={({ field }) => (
                      <FormItem className="min-w-0 flex-1">
                        <FormControl>
                          <Input
                            {...field}
                            id={descriptorId}
                            aria-label={statementLabel}
                            placeholder={OGSM_COPY.objectivePlaceholder}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>
              {goalType === 'TEXT' && (
                <FormField
                  key="text"
                  control={form.control}
                  name="targetText"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex items-baseline justify-between gap-2">
                        <FormLabel>
                          {OGSM_COPY.goalStatement}
                          <Required />
                        </FormLabel>
                        <Typography
                          variant="body-sm"
                          as="span"
                          className="text-primary/50 tabular-nums"
                        >
                          {OGSM_COPY.textCount(targetText.length)}
                        </Typography>
                      </div>
                      <FormControl>
                        <Textarea
                          {...field}
                          rows={3}
                          maxLength={TEXT_GOAL_MAX}
                          placeholder={OGSM_COPY.textGoalPlaceholder}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
              <div className="grid gap-4 sm:grid-cols-2">{dateField}</div>
            </div>
          ) : (
            <div
              key={isMetric ? 'metric' : 'titled'}
              className="flex flex-col gap-4"
            >
              {isMetric ? (
                <FormField
                  control={form.control}
                  name="metricLabelId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {OGSM_COPY.metricField}
                        <Required />
                      </FormLabel>
                      <MetricCombobox
                        options={metrics.options}
                        value={field.value || undefined}
                        isLoading={metrics.isLoading}
                        triggerClassName="w-full"
                        onValueChange={field.onChange}
                      />
                      <FormMessage />
                    </FormItem>
                  )}
                />
              ) : (
                <FormField
                  control={form.control}
                  name="title"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {OGSM_COPY.titleLabel}
                        <Required />
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          aria-label={OGSM_COPY.titleLabel}
                          placeholder={OGSM_COPY.customTitlePlaceholder}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
              {directionField}
              <div className="grid gap-4 sm:grid-cols-2">
                {goalValueField}
                {dateField}
              </div>
              {isMetric && (
                <div className="flex items-baseline gap-3 rounded-md bg-muted px-3 py-2.5">
                  <Typography
                    variant="body-sm"
                    as="span"
                    className="shrink-0 text-primary/50"
                  >
                    {OGSM_COPY.titleLabel}
                  </Typography>
                  <Typography
                    variant="h6"
                    as="p"
                    className="min-w-0 break-words leading-normal"
                  >
                    {preview}
                  </Typography>
                </div>
              )}
            </div>
          )}

          {mode === 'root' && isMetric && (
            <FormField
              control={form.control}
              name="includeInScorecard"
              render={({ field }) => (
                <FormItem className="flex-row items-start gap-2">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={(checked) =>
                        field.onChange(checked === true)
                      }
                    />
                  </FormControl>
                  <FormLabel className="font-normal">
                    {OGSM_COPY.includeOnPublish}
                  </FormLabel>
                </FormItem>
              )}
            />
          )}

          {create.isError && <FormError error={create.error} />}
        </FormBody>

        <FormFooter>
          <Button type="button" variant="outline" onClick={onDone}>
            {OGSM_COPY.cancel}
          </Button>
          <Button type="submit" disabled={create.isPending}>
            {create.isPending ? OGSM_COPY.creating : submitLabel}
          </Button>
        </FormFooter>
      </form>
    </Form>
  )
}

function EditObjectiveForm({
  companyId,
  session,
  node,
  onDone,
  onRevise,
}: {
  companyId: string
  session: OgsmSession
  node: OgsmBoardNode
  onDone: () => void
  /** Absent when the goal cannot be revised from here. */
  onRevise?: () => void
}) {
  const api = useOgsmApi(companyId)
  const { metricLinking } = api
  const update = useUpdateOgsmNodeMutation(api, session.id)
  const isNumeric = isNumericTarget(node.targetType)
  const showMetric = editsMetric(node, metricLinking)
  const metrics = useMetricOptions(
    companyId,
    showMetric,
    node.metricLabelId ?? undefined
  )
  const form = useForm<EditObjectiveValues>({
    resolver: standardSchemaResolver(editObjectiveSchema),
    defaultValues: defaultEditObjectiveValues(node),
  })
  const [title, direction, metricLabelId] = useWatch({
    control: form.control,
    name: ['title', 'direction', 'metricLabelId'],
  })
  const metric = findMetric(metrics.metricOptions, metricLabelId)
  const note =
    node.targetType === 'DATE'
      ? OGSM_COPY.qualitativeNote
      : node.targetType === 'TEXT'
        ? OGSM_COPY.textNote
        : metric &&
          OGSM_COPY.numericNote(
            metric.labeledAs,
            metricUnitLabel(metric.dataType)
          )
  const changed =
    Object.keys(
      toUpdateObjectiveInput(
        node,
        { title, direction, metricLabelId },
        metricLinking
      )
    ).length > 0
  const goal = formatTarget(
    node,
    { value: node.targetValue, text: node.targetText, date: node.targetDate },
    true
  )

  return (
    <Form {...form}>
      <form
        className="flex min-h-0 flex-1 flex-col"
        onSubmit={form.handleSubmit((values) =>
          update.mutate(
            {
              nodeId: node.id,
              input: toUpdateObjectiveInput(node, values, metricLinking),
            },
            { onSuccess: onDone }
          )
        )}
      >
        <FormBody>
          <LockedGoalType type={goalTypeOf(node)} />

          <div className="flex flex-col gap-2 rounded-lg border border-primary/10 p-3">
            <Typography variant="overline" className="text-primary/60">
              {node.depth === 0
                ? OGSM_COPY.theObjective
                : OGSM_COPY.theStrategy}
            </Typography>
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      {...field}
                      aria-label={OGSM_COPY.titleLabel}
                      placeholder={OGSM_COPY.titlePlaceholder}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {isNumeric && (
              <>
                <FormField
                  control={form.control}
                  name="direction"
                  render={({ field }) => (
                    <FormItem>
                      <DirectionControl
                        value={field.value}
                        onChange={field.onChange}
                      />
                    </FormItem>
                  )}
                />
                {showMetric && (
                  <FormField
                    control={form.control}
                    name="metricLabelId"
                    render={({ field }) => (
                      <FormItem>
                        <MetricCombobox
                          options={metrics.options}
                          value={field.value ?? undefined}
                          isLoading={metrics.isLoading}
                          triggerClassName="w-full"
                          onValueChange={field.onChange}
                        />
                      </FormItem>
                    )}
                  />
                )}
              </>
            )}
            {note && <NoteBox>{note}</NoteBox>}
          </div>

          <Separator className="bg-primary/10" />

          <div className="flex flex-col gap-2">
            <Label>{OGSM_COPY.goalValue}</Label>
            <div className="flex items-center justify-between gap-4">
              <Typography
                variant="h6"
                className="min-w-0 break-words font-semibold leading-snug tabular-nums"
              >
                {goal || OGSM_COPY.noGoal}
              </Typography>
              {onRevise && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={changed}
                  onClick={onRevise}
                >
                  <Pencil />
                  {node.depth === 0
                    ? OGSM_COPY.reviseGoal
                    : OGSM_COPY.reviseMeasure}
                </Button>
              )}
            </div>
            {changed && onRevise && (
              <Typography variant="body-sm" className="text-primary/60">
                {OGSM_COPY.saveOrCancelFirst}
              </Typography>
            )}
          </div>

          {update.isError && <FormError error={update.error} />}
        </FormBody>

        <FormFooter>
          <Button type="button" variant="outline" onClick={onDone}>
            {OGSM_COPY.cancel}
          </Button>
          <Button type="submit" disabled={update.isPending || !changed}>
            {update.isPending ? OGSM_COPY.saving : OGSM_COPY.saveChanges}
          </Button>
        </FormFooter>
      </form>
    </Form>
  )
}

function dialogTitle(target: ObjectiveDialogTarget): string {
  if (target.mode === 'create') return OGSM_COPY.newObjective
  if (target.mode === 'create-child') return OGSM_COPY.newStrategy
  return target.node.depth === 0
    ? OGSM_COPY.editObjective
    : OGSM_COPY.editStrategy
}

function formKey(target: ObjectiveDialogTarget): string {
  if (target.mode === 'create-child') return `child:${target.parent.id}`
  if (target.mode === 'edit') return `edit:${target.node.id}`
  return 'create'
}

/**
 * Creates a root objective or a child of any node from one "Goal type" picker
 * (`goalTypesFor`), or edits a node's title, direction and metric. The
 * workspace and "Add strategy" open the create modes, the board opens edit;
 * the goal itself only changes through "Revise goal".
 */
export function NewObjectiveDialog({
  companyId,
  session,
  target,
  onClose,
  onRevise,
}: NewObjectiveDialogProps) {
  // The header keeps the last target so it does not blank out while closing.
  const [shown, setShown] = useState(target)
  if (target && target !== shown) setShown(target)
  const header = target ?? shown
  const opener = useRef<HTMLElement | null>(null)
  const reviseNext = useRef<OgsmBoardNode | null>(null)

  return (
    <Dialog
      open={Boolean(target)}
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
    >
      <OgsmDialogContent
        aria-describedby={undefined}
        onOpenAutoFocus={() => {
          const active = document.activeElement
          opener.current = active instanceof HTMLElement ? active : null
        }}
        onCloseAutoFocus={(event) => {
          // Radix returns focus only to a DialogTrigger, and every opener here is a plain button.
          if (opener.current?.isConnected) {
            event.preventDefault()
            opener.current.focus()
          }
          // Opened after focus is back on the opener, so the revise dialog returns it there too.
          const next = reviseNext.current
          reviseNext.current = null
          if (next) onRevise?.(next)
        }}
      >
        <OgsmDialogHeader
          title={header ? dialogTitle(header) : OGSM_COPY.newObjective}
        >
          {header?.mode === 'create-child' && (
            <Typography variant="body-sm" className="text-primary/60">
              {OGSM_COPY.under(header.parent.title)}
            </Typography>
          )}
        </OgsmDialogHeader>
        {/* Mounted per open, so every open starts from a fresh form and an idle request. */}
        {target?.mode === 'edit' ? (
          <EditObjectiveForm
            key={formKey(target)}
            companyId={companyId}
            session={session}
            node={target.node}
            onDone={onClose}
            onRevise={
              onRevise && session.structureEditable
                ? () => {
                    reviseNext.current = target.node
                    onClose()
                  }
                : undefined
            }
          />
        ) : (
          target && (
            <CreateObjectiveForm
              key={formKey(target)}
              companyId={companyId}
              session={session}
              parent={
                target.mode === 'create-child' ? target.parent : undefined
              }
              onDone={onClose}
            />
          )
        )}
      </OgsmDialogContent>
    </Dialog>
  )
}
